import React, { useState } from 'react';
import { Button, Container } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { useAuth } from '../../api/AuthProvider';
import { useUser } from '../../api/hooks/useUser';
import Anchor from '../../components/Anchor/Anchor';
import { useDictionary } from '../../hooks/useDictionary';
import { ButtonVariant } from '../../styles/buttons';
import { colors } from '../../styles/colors';
import LinkViewModel from '../../view-models/LinkViewModel';
import MobileMenuModal from './MobileMenuModal';
import HeaderBrand from './HeaderBrand';
import { FontFamily, FontWeight } from '../../styles/typography';

export type MobileNavigationProps = {
  loggedIn?: boolean;
  logo?: LinkViewModel;
  navigationLinks?: LinkViewModel[];
  serviceMenuLinks?: LinkViewModel[];
};

function MobileLogoutLink({
  loggedIn,
}: Pick<MobileNavigationProps, 'loggedIn'>) {
  const { authenticatedLogoutUrl, logoutUrl } = useAuth();
  const { user, userReady } = useUser();
  const { t } = useDictionary();

  const showLogout = userReady ? !!user : loggedIn === true;
  const isReady = loggedIn === true || userReady;
  const href = userReady ? logoutUrl : authenticatedLogoutUrl;
  const shouldRenderLogout = showLogout || !isReady;

  return (
    <span
      data-auth-logout-placeholder={!isReady ? '' : undefined}
      style={{ display: !isReady ? 'none' : undefined }}
    >
      {shouldRenderLogout ? (
        <Anchor
          external
          prefetch={false}
          href={href}
          label={t('ui.logout')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: rem(32),
            px: rem(16),
            py: rem(6),
            borderRadius: rem(80),
            backgroundColor: 'rgba(22, 80, 80, 0.2)',
            color: colors.base.Text.PrimaryStatic,
            fontFamily: FontFamily.Heading,
            fontSize: rem(14),
            lineHeight: rem(20),
            fontWeight: FontWeight.Medium,
            textDecoration: 'none',
          }}
        />
      ) : null}
    </span>
  );
}

function MobileNavigation({ logo, loggedIn, ...rest }: MobileNavigationProps) {
  const { t } = useDictionary();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Container
        as="header"
        sx={{
          display: ['block', 'none'],
          position: 'sticky',
          top: 0,
          height: '100%',
          bg: colors.components.fixedTop.Background.Default,
          px: 0,
          zIndex: 1,
        }}
      >
        <div
          css={{
            height: rem(56),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Anchor
            prefetch
            href={logo?.href ?? '/'}
            aria-label={logo?.label}
            sx={{ ml: rem(20), textDecoration: 'none' }}
          >
            <HeaderBrand />
          </Anchor>
          <div
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              mr: rem(20),
            }}
          >
            <MobileLogoutLink loggedIn={loggedIn} />
            <Button
              id="mobile-menu-trigger"
              aria-label={t('ui.openMenu')}
              variant={ButtonVariant.Flat}
              onClick={() => setIsOpen(true)}
              sx={{
                position: 'relative',
                height: rem(56),
                width: 'auto',
                minWidth: 'auto',
                px: 0,
                py: 0,
                mr: 0,
                mt: 0,
                color: colors.base.Text.PrimaryStatic,
                fontFamily: FontFamily.Heading,
                fontSize: rem(16),
                lineHeight: rem(24),
                fontWeight: FontWeight.Medium,
                '&:hover': {
                  textDecoration: 'none',
                },
                '&:focus-visible, &[data-focus-visible-added]': {
                  outline: 'none',
                },
                '&:focus-visible [data-menu-label], &[data-focus-visible-added] [data-menu-label]': {
                  outline: `${rem(3)} solid ${
                    colors.components.button.Quarternary.Background.Focus
                  }`,
                  outlineOffset: rem(2),
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: rem(-12),
                  right: rem(-12),
                },
              }}
            >
              <span data-menu-label>{t('ui.menuDialog')}</span>
            </Button>
          </div>
        </div>
      </Container>
      <MobileMenuModal
        {...rest}
        loggedIn={loggedIn}
        onDismiss={() => setIsOpen(false)}
        isOpen={isOpen}
      />
    </>
  );
}

export default MobileNavigation;
